import { Component, HostListener, Inject, OnDestroy, TemplateRef, ViewChild, ViewEncapsulation } from '@angular/core';
import { Subscription } from 'rxjs';
import { SearchBarService } from 'src/app/shared/components/search-bar/search-bar.service';
import { Choice } from 'src/app/shared/components/select/choice.model';
import { TableConfig, TableFilters } from 'src/app/shared/components/table/shared/models';

import { MatDialog } from '@angular/material/dialog';
import { finalize } from 'rxjs/operators';
import { CustomRouter } from 'src/admin-front/ajs-upgraded-providers';
import { ConfirmDialogComponent } from 'src/app/shared/components/confirm-dialog/confirm-dialog.component';
import { TableColumnTypeEnum } from 'src/app/shared/components/table/shared/enums';
import { BasicService } from 'src/app/shared/services/basic.service';
import { TrackLinkLabel } from './shared/models/track-link-label.model';
import { TrackLinkLabelsService } from './track-link-labels.service';

@Component({
  selector: 'app-track-link-labels',
  templateUrl: './track-link-labels.component.html',
  styleUrls: ['./track-link-labels.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class TrackLinkLabelsComponent implements OnDestroy {
  selectedTabIndex = 0;
  config: TableConfig = {
    displayedColumns: ['id', 'label', 'description', 'actions'],
    columns: [
      { name: 'id', translationKey: 'parameters/track-link-labels/id', property: 'id' },
      {
        name: 'label',
        translationKey: 'parameters/track-link-labels/label',
        property: 'labelLink',
        type: TableColumnTypeEnum.LINK,
        sortEnabled: true,
      },
      { name: 'description', translationKey: 'parameters/track-link-labels/description', property: 'description' },
    ],
    actions: [
      {
        tooltip: 'delete',
        clickFn: (element: TrackLinkLabel) => this.delete(element.id),
        icon: 'delete',
        color: 'warn',
      },
    ],
  };
  filters: TableFilters = {};
  templatesChoices: Choice[] = [];

  itemToDelete: TrackLinkLabel;
  @ViewChild('templateDependenciesTemplateRef') templateDependenciesTemplateRef: TemplateRef<any>;

  private subscriptions = new Subscription();

  @HostListener('window:resize')
  onWindowResizeUpdateActionsPosition() {
    this.searchBarService.checkScrollPosition();
  }

  constructor(
    public dialog: MatDialog,
    private trackLinkLabelsService: TrackLinkLabelsService,
    private basicService: BasicService,
    private searchBarService: SearchBarService,
    private customRouter: CustomRouter,
    @Inject('TopToolbarFactory') private topToolbarService: any
  ) {
    topToolbarService.setTitleTranslation('parameters/track-link-labels/title');
    this.searchBarService.sort = { active: 'label', direction: 'asc' };
    this.searchBarService.referer$ = trackLinkLabelsService.retrieveTrackLinkLabels;
    this.searchBarService.formatter = (element: TrackLinkLabel) => {
      element['labelLink'] = {
        text: element.label,
        clickFn: this.seeTrackLinkLabel,
        clickFnParam: element.id,
        newTab: false,
      };
    };
    this.searchBarService.submitQuery();
  }

  delete = async (trackLinkLabelId: number) => {
    this.basicService.showSpinner();
    this.itemToDelete = null;
    const templates = await this.trackLinkLabelsService
      .getTemplatesDependencies(trackLinkLabelId)
      .pipe(finalize(() => this.basicService.hideSpinner()))
      .toPromise();
    const item: TrackLinkLabel = this.searchBarService.itemsBS.value.find(({ id }) => id === trackLinkLabelId);
    if (item) {
      this.itemToDelete = item;
      const hasDepedendencies = templates?.length;
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        panelClass: ['medium-size'],
        autoFocus: false,
        data: {
          title: this.basicService.translate(`parameters/track-link-labels/delete-title`),
          templateRef: this.templateDependenciesTemplateRef,
          templateRefInputs: {
            templates,
          },
          description: this.basicService.translate(
            hasDepedendencies
              ? 'parameters/track-link-labels/has-depedendencies-description'
              : 'parameters/track-link-labels/delete-description'
          ),
          confirmBtnText: hasDepedendencies ? undefined : this.basicService.translate('delete'),
          cancelBtnText: this.basicService.translate('close'),
        },
      });
      dialogRef.afterClosed().subscribe(result => {
        if (result) {
          if (item) {
            const { id } = item;
            this.basicService.showSpinner();
            this.trackLinkLabelsService
              .deleteTrackLinkLabel(id)
              .pipe(finalize(() => this.basicService.hideSpinner()))
              .subscribe({
                next: () => {
                  this.searchBarService.submitQuery();
                },
              });
          }
        }
      });
    }
  };

  seeTrackLinkLabel = (trackLinkLabelId: number) => {
    const item: TrackLinkLabel = this.searchBarService.itemsBS.value.find(({ id }) => id === trackLinkLabelId);
    if (item) {
      const { id: trackLinkLabelId } = item;
      this.customRouter.go('auth.alcmeon.fuse.parameters.track-link-labels.details', {
        trackLinkLabelId,
      });
    }
  };

  primaryButtonAction = () => this.customRouter.go('auth.alcmeon.fuse.parameters.track-link-labels.create');

  ngOnDestroy(): void {
    this.subscriptions?.unsubscribe();
    this.searchBarService.onDidLeave();
    this.topToolbarService.clearTitle();
  }
}
